import analyzeIndexes from './analyze/analyzeIndexes';
import id from './id';
import type { ActualCollection, Collection, CollectionStats } from './models/collection';
import type { Database } from './models/database';
import { type Index, type IndexStats, type IndexStatsSummary } from './models/indexes';
import type { State } from './models/state';
import orderBy from './orderBy';

const emptyCollStats: CollectionStats = {
  count: 0,
  storageStats: {
    indexSizes: {},
    size: 0,
    storageSize: 0,
    storageCacheSize: 0,
    storageCachePageReads: 0,
    totalIndexSize: 0,
    totalIndexCacheSize: 0,
    totalIndexCachePageReads: 0,
    totalSize: 0,
  },
};

const emptyIndexStats: IndexStatsSummary = {
  ops: 0,
  since: Infinity,
};

function idIndex({ db, coll }: { db: string; coll: string }): Index {
  return {
    db,
    coll,
    name: '_id_',
    isDefined: true,
    implicit: true,
    hash: '_id_',
    spec: {
      name: '_id_',
      key: { _id: 1 },
    },
    actual: [],
    statsSummary: emptyIndexStats,
    issues: [],
  };
}

function isImplicitIndex({ timeseries }: ActualCollection, name: string) {
  if (!timeseries) {
    return name === '_id_';
  }

  // since 6.3 a timeseries collection with a metaField gets a meta/time index; it has no _id_ index
  return !!timeseries.metaField && name === `${timeseries.metaField}_1_${timeseries.timeField}_1`;
}

export default function mergeCollections(state: State): Database[] {
  const collections = [...state.definitions.collections.values()].map<Collection>((collection) => ({
    ...collection,
    isDefined: true,
    indexes: [
      idIndex(collection),
      ...collection.indexes.map((index) => ({
        db: collection.db,
        coll: collection.coll,
        name: index.spec.name ?? '',
        isDefined: true,
        ...index,
        actual: [],
        statsSummary: emptyIndexStats,
        issues: [],
      })),
    ],
    actual: [],
    stats: emptyCollStats,
    recommendations: [],
  }));

  const collLookup = new Map(
    collections.map((collection) => {
      return [id({ db: collection.db, coll: collection.coll }), collection];
    }),
  );

  for (const collection of state.actual.collections.values()) {
    let db = collection.db;
    let item = collLookup.get(id(collection));
    let match;

    if (!item) {
      const database = [...state.definitions.databases.values()].find((database) => db.match(`^${database.match}$`));

      if (database) {
        db = database.db;
        match = database.match;
        item = collLookup.get(id({ db, coll: collection.coll }));
      }
    }

    if (!item) {
      item = {
        db,
        coll: collection.coll,
        isDefined: false,
        match,
        indexes: [idIndex({ db, coll: collection.coll })],
        actual: [],
        stats: emptyCollStats,
        recommendations: [],
      };

      collections.push(item);
      collLookup.set(id(item), item);
    }

    item.actual.push(collection);

    for (const index of collection.indexes) {
      let indexItem = item.indexes.find((x) => x.name === index.spec.name);

      if (!indexItem) {
        const implicit = isImplicitIndex(collection, index.spec.name ?? '');
        indexItem = {
          db: item.db,
          coll: item.coll,
          name: index.spec.name ?? '',
          isDefined: implicit,
          implicit: implicit || undefined,
          hash: index.hash,
          spec: index.spec,
          actual: [],
          statsSummary: emptyIndexStats,
          issues: [],
        };
        item.indexes.push(indexItem);
      }

      indexItem.actual.push({
        ...index,
        statsSummary: aggregateIndexStats([index]),
      });
    }
  }

  for (const collection of collections) {
    // the _id_ placeholder only stands in for an existing collection that really has one (not timeseries or clustered)
    collection.indexes = orderBy(
      collection.indexes.filter((index) => !(index.implicit && index.actual.length === 0 && collection.actual.length > 0)),
      'name',
    );
    collection.stats = aggregateCollStats(collection.actual);

    const { issues, recommendations } = analyzeIndexes(collection.indexes);
    collection.recommendations = recommendations;

    for (const index of collection.indexes) {
      index.statsSummary = aggregateIndexStats(index.actual);
      index.issues = Array.from(issues.get(index.name) ?? []);
    }
  }
  calculateRelativeValues(collections);

  let databases = Map.groupBy(collections, (item) => item.db)
    .entries()
    .map(([db, collections]) => ({
      db,
      collections,
      stats: aggregateCollStats(collections),
    }))
    .toArray();

  calculateRelativeValues(databases);

  return databases;
}

export function aggregateCollStats(items: { stats?: CollectionStats }[]) {
  const stats = items.reduce((acc, item) => {
    acc.count += item.stats?.count ?? 0;
    acc.storageStats.size += item.stats?.storageStats.size ?? 0;
    acc.storageStats.storageSize += item.stats?.storageStats.storageSize ?? 0;
    acc.storageStats.totalSize += item.stats?.storageStats.totalSize ?? 0;
    acc.storageStats.storageCacheSize = (acc.storageStats.storageCacheSize ?? 0) + (item.stats?.storageStats.storageCacheSize ?? 0);
    acc.storageStats.storageCachePageReads =
      (acc.storageStats.storageCachePageReads ?? 0) + (item.stats?.storageStats.storageCachePageReads ?? 0);
    acc.storageStats.totalIndexSize += item.stats?.storageStats.totalIndexSize ?? 0;
    acc.storageStats.totalIndexCacheSize =
      (acc.storageStats.totalIndexCacheSize ?? 0) + (item.stats?.storageStats.totalIndexCacheSize ?? 0);
    acc.storageStats.totalIndexCachePageReads =
      (acc.storageStats.totalIndexCachePageReads ?? 0) + (item.stats?.storageStats.totalIndexCachePageReads ?? 0);

    for (const [name, size] of Object.entries(item.stats?.storageStats.indexSizes ?? {})) {
      acc.storageStats.indexSizes[name] = (acc.storageStats.indexSizes[name] ?? 0) + size;
    }

    return acc;
  }, structuredClone(emptyCollStats));

  return stats;
}

export function aggregateIndexStats(items: { stats?: IndexStats[] }[]) {
  const stats = items
    .flatMap((x) => x.stats ?? [])
    .reduce((acc, item) => {
      acc.ops += item.accesses.ops;
      acc.since = Math.min(acc.since, item.accesses.since.getTime());

      return acc;
    }, structuredClone(emptyIndexStats));

  return stats;
}

export function calculateRelativeValues<T extends { stats: CollectionStats }>(items: Iterable<T>) {
  const max = Iterator.from(items).reduce(
    (acc, item) => {
      acc.storageSize = Math.max(acc.storageSize, item.stats.storageStats.storageSize);
      acc.storageCacheSize = Math.max(acc.storageCacheSize, item.stats.storageStats.storageCacheSize ?? 0);
      acc.totalIndexSize = Math.max(acc.totalIndexSize, item.stats.storageStats.totalIndexSize);
      acc.totalIndexCacheSize = Math.max(acc.totalIndexCacheSize, item.stats.storageStats.totalIndexCacheSize ?? 0);
      return acc;
    },
    { storageSize: 0, storageCacheSize: 0, totalIndexSize: 0, totalIndexCacheSize: 0 },
  );

  for (const item of items) {
    item.stats.storageStats.storageSizeRelative = item.stats.storageStats.storageSize / max.storageSize;
    item.stats.storageStats.storageCacheSizeRelative = (item.stats.storageStats.storageCacheSize ?? 0) / max.storageCacheSize;
    item.stats.storageStats.totalIndexSizeRelative = item.stats.storageStats.totalIndexSize / max.totalIndexSize;
    item.stats.storageStats.totalIndexCacheSizeRelative = (item.stats.storageStats.totalIndexCacheSize ?? 0) / max.totalIndexCacheSize;
  }
}
